import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  faArrowLeft,
  faCircleXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import { Formik, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import useAxiosRequests from "../hooks/useAxiosRequest";
import ExternalRoutes from "../config/ExternalRoutes";
import { useNavigate } from "react-router";
import { MessageContext } from "../context/MessageContext";
import AuthContext from "../context/AuthContext";
import { CircularProgress } from "@mui/material";

const FileInput = ({ field, form, ...props }) => {
  const [inputKey, setInputKey] = useState(Date.now());
  useEffect(() => {
    if (!form.values[field.name]) {
      setInputKey(Date.now());
    }
  }, [form.values[field.name]]);

  const handleChange = (event) => {
    const file = event.target.files[0];
    form.setFieldValue(field.name, file || null);
    form.setFieldTouched(field.name, true);
  };

  return (
    <Form.Control
      key={inputKey}
      type="file"
      // accept="image/jpeg,image/jpg,image/png,application/pdf,text/plain"
      onChange={handleChange}
      {...props}
    />
  );
};

const Entreprise = () => {
  const { showSuccess, showError } = useContext(MessageContext);
  const { userInfo } = useContext(AuthContext);
  const { sendRequest } = useAxiosRequests();
  const navigate = useNavigate();
  const [documentData, setDocumentData] = useState([]);
  const [societiesData, setSocietiesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const validationSchema = Yup.object().shape({
    nom: Yup.string().required("Le nom est requis"),
    nSiret: Yup.number()
      .typeError("Le N° SIRET doit être un nombre")
      .required("Le N° SIRET est requis")
      .min(10000000000000, "Le N° SIRET doit contenir 14 chiffres")
      .max(99999999999999, "Le N° SIRET doit contenir 14 chiffres"),
    adresse: Yup.string().required("L'adresse est requise"),
    fichier: Yup.mixed()
      .nullable()
      .test("fileSize", "Fichier trop volumineux (12MB max)", value => !value || value.size <= 12 * 1024 * 1024)
      .test("fileType", "Format non supporté (JPEG, PNG, PDF ,JPG)", value =>
        !value || ["application/pdf", "application/x-pdf", "image/png", "image/jpeg", "text/plain"]
          .includes(value.type)
      ),
    rib: Yup.mixed()
      .nullable()
      .test("fileSize", "Fichier trop volumineux (12MB max)", value => !value || value.size <= 12 * 1024 * 1024)
      .test("fileType", "Format non supporté (JPEG, PNG, PDF ,JPG)", value =>
        !value || ["application/pdf", "application/x-pdf", "image/png", "image/jpeg", "text/plain"]
          .includes(value.type)
      ),
  });

  const fetchDataSociety = useCallback(async () => {
    try {
      const response = await sendRequest({
        method: "GET",
        url: ExternalRoutes.society.get_by_user + userInfo.id,
      });

      if (response) {
        setSocietiesData(response.member);
      }
    } catch (error) {
    }
  }, [sendRequest]);

  const fetchData = useCallback(async () => {
    try {
      const response = await sendRequest({
        method: "GET",
        url: ExternalRoutes.document.list_by_user + userInfo.id,
      });

      if (response) {
        setDocumentData(response.member);
      }
    } catch (error) {
    }
  }, [sendRequest]);

  useEffect(() => {
    fetchData();
    fetchDataSociety()
  }, []);

  const onHandleSubmit = async (values, { setSubmitting, resetForm }) => {
    setLoading(true);
    setHasSubmitted(true);
    try {
      const body = new FormData();
      Object.keys(values).forEach((key) => {
        if (values[key] !== null && values[key] !== undefined) {
          body.append(key, values[key]);
        }
      });

      const response = await sendRequest({
        method: "POST",
        url: ExternalRoutes.society.add,
        body,
      });

      if (!response) return;

      if (response.status === "success") {
        showSuccess(response.message);
        navigate("/entreprise/add");
        fetchData();
        fetchDataSociety()
        resetForm();
      } else {
        showError(response.message || "Erreur inattendue.");
      }
    } catch (error) {
      showError("Une erreur est survenue.");
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };


  const handleDeleteDocument = async (documents) => {
    navigate('/delete-confirmation', { state: { documents } });
  };

  const handleViewDocument = async values => {
    navigate('/view-document', { state: { values } })
  }



  return (
    <section>
      <div className="kl-admin-header">
        <button onClick={() => navigate("/customer")} className="p-0 kl-back-button">
          <FontAwesomeIcon icon={faArrowLeft} className="kl-icon-arrow" />
        </button>
        <div className="kl-logo-contab"><span>Entreprise</span></div>
        <button className="kl-button-profile"></button>
      </div>

      <div className="kl-title-other-info text-center"><span>Informations</span></div>

      <Formik
        enableReinitialize
        initialValues={{
          nom: societiesData[0]?.sctName || "",
          nSiret: societiesData[0]?.sctSiret || "",
          adresse: societiesData[0]?.sctAddress || "",
          fichier: null,
          rib: null,
        }}
        validationSchema={validationSchema}
        onSubmit={onHandleSubmit}
        validateOnMount
        validateOnChange
      >
        {({ handleSubmit,
          isValid,
          isSubmitting,
          setFieldValue,
          values,
          errors,
          touched }) => (
          <form
            noValidate
            autoComplete="off"
            onSubmit={handleSubmit}
          >
            <div className="kl-form-control-entreprise">
              <Form.Group className="kl-form-group">
                <Form.Label>Nom</Form.Label>
                <Field as={Form.Control} name="nom" className={`kl-input-form ${touched.nom && errors.nom ? "is-invalid" : ""}`} />
                <ErrorMessage name="nom" component="small" className="text-danger" />
              </Form.Group>
              <Form.Group className="kl-form-group">
                <Form.Label>N° SIRET</Form.Label>
                <Field as={Form.Control} type="number" name="nSiret" className={`kl-input-form ${touched.nSiret && errors.nSiret ? "is-invalid" : ""}`} />
                <ErrorMessage name="nSiret" component="small" className="text-danger" />
              </Form.Group>
              <Form.Group className="kl-form-group">
                <Form.Label>Adresse</Form.Label>
                <Field as={Form.Control} name="adresse" className={`kl-input-form ${touched.adresse && errors.adresse ? "is-invalid" : ""}`} />
                <ErrorMessage name="adresse" component="small" className="text-danger" />
              </Form.Group>
            </div>

            <div className="kl-title-other-info-doc text-center"><span>Documents</span></div>

            <div className="kl-content-file-society">
              <div className="kl-import-document">
                <strong>Kbis</strong>
                <div className="kl-display-doc">
                  <div className="d-flex justify-content-center">
                    <div className="kl-icon-file-up">
                      <svg width="33" height="32" viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M27.833 13.5865H23.9797C20.8197 13.5865 18.2463 11.0132 18.2463 7.85317V3.99984C18.2463 3.2665 17.6463 2.6665 16.913 2.6665H11.2597C7.15301 2.6665 3.83301 5.33317 3.83301 10.0932V21.9065C3.83301 26.6665 7.15301 29.3332 11.2597 29.3332H21.7397C25.8463 29.3332 29.1663 26.6665 29.1663 21.9065V14.9198C29.1663 14.1865 28.5663 13.5865 27.833 13.5865ZM15.873 18.0398C15.673 18.2398 15.4197 18.3332 15.1663 18.3332C14.913 18.3332 14.6597 18.2398 14.4597 18.0398L13.4997 17.0798V22.6665C13.4997 23.2132 13.0463 23.6665 12.4997 23.6665C11.953 23.6665 11.4997 23.2132 11.4997 22.6665V17.0798L10.5397 18.0398C10.153 18.4265 9.51301 18.4265 9.12634 18.0398C8.73967 17.6532 8.73967 17.0132 9.12634 16.6265L11.793 13.9598C11.8863 13.8798 11.9797 13.8132 12.0863 13.7598C12.113 13.7465 12.153 13.7332 12.1797 13.7198C12.2597 13.6932 12.3397 13.6798 12.433 13.6665C12.473 13.6665 12.4997 13.6665 12.5397 13.6665C12.6463 13.6665 12.753 13.6932 12.8597 13.7332C12.873 13.7332 12.873 13.7332 12.8863 13.7332C12.993 13.7732 13.0997 13.8532 13.1797 13.9332C13.193 13.9465 13.2063 13.9465 13.2063 13.9598L15.873 16.6265C16.2597 17.0132 16.2597 17.6532 15.873 18.0398Z" fill="#5379F6" />
                        <path d="M23.7399 11.7468C25.0066 11.7602 26.7666 11.7602 28.2732 11.7602C29.0332 11.7602 29.4332 10.8668 28.8999 10.3335C26.9799 8.40015 23.5399 4.92015 21.5666 2.94682C21.0199 2.40015 20.0732 2.77349 20.0732 3.53349V8.18682C20.0732 10.1335 21.7266 11.7468 23.7399 11.7468Z" fill="#5379F6" />
                      </svg>
                    </div>
                  </div>
                  <div className="kl-text-import">
                    <h2>Importer un document</h2><span>Max. 12 MB</span>
                  </div>
                  <label htmlFor="fichier" className="btn btn-secondary kl-btn-file-secondary">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12.7164 18.5417H7.28307C3.19141 18.5417 1.44141 16.7917 1.44141 12.7001V12.5917C1.44141 8.89173 2.89974 7.1084 6.16641 6.80006C6.49974 6.77506 6.81641 7.02506 6.84974 7.36673C6.88307 7.7084 6.63307 8.01673 6.28307 8.05006C3.66641 8.29173 2.69141 9.52506 2.69141 12.6001V12.7084C2.69141 16.1001 3.89141 17.3001 7.28307 17.3001H12.7164C16.1081 17.3001 17.3081 16.1001 17.3081 12.7084V12.6001C17.3081 9.5084 16.3164 8.27506 13.6497 8.05006C13.3081 8.01673 13.0497 7.71673 13.0831 7.37506C13.1164 7.0334 13.4081 6.77506 13.7581 6.8084C17.0747 7.09173 18.5581 8.8834 18.5581 12.6084V12.7167C18.5581 16.7917 16.8081 18.5417 12.7164 18.5417Z" fill="#5379F6" />
                      <path d="M10 13.1249C9.65833 13.1249 9.375 12.8416 9.375 12.4999V3.0166C9.375 2.67493 9.65833 2.3916 10 2.3916C10.3417 2.3916 10.625 2.67493 10.625 3.0166V12.4999C10.625 12.8416 10.3417 13.1249 10 13.1249Z" fill="#5379F6" />
                      <path d="M12.7912 5.50003C12.6329 5.50003 12.4745 5.4417 12.3495 5.3167L9.99954 2.9667L7.64954 5.3167C7.40788 5.55837 7.00788 5.55837 6.76621 5.3167C6.52454 5.07503 6.52454 4.67503 6.76621 4.43337L9.55788 1.6417C9.79954 1.40003 10.1995 1.40003 10.4412 1.6417L13.2329 4.43337C13.4745 4.67503 13.4745 5.07503 13.2329 5.3167C13.1162 5.4417 12.9495 5.50003 12.7912 5.50003Z" fill="#5379F6" />
                    </svg>
                    <span>
                      Importer</span></label>
                  <Field id="fichier" name="fichier" component={FileInput} style={{ display: "none" }} />
                  <ErrorMessage name="fichier" component="small" className="text-danger mt-3" />
                </div>
                {values.fichier && (
                  <div className="kl-display-pdf-1">
                    <div className="kl-content-display">
                      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13.7782 24.4668C13.4715 24.4668 13.2649 24.4968 13.1582 24.5268V26.4901C13.2849 26.5201 13.4432 26.5285 13.6615 26.5285C14.4599 26.5285 14.9515 26.1251 14.9515 25.4435C14.9515 24.8335 14.5282 24.4668 13.7782 24.4668ZM19.5899 24.4868C19.2565 24.4868 19.0399 24.5168 18.9115 24.5468V28.8968C19.0399 28.9268 19.2465 28.9268 19.4332 28.9268C20.7949 28.9368 21.6815 28.1868 21.6815 26.6001C21.6915 25.2168 20.8832 24.4868 19.5899 24.4868Z" fill="#DB291F" />
                        <path d="M23.9204 3.91977C23.5453 3.5447 23.0366 3.33398 22.5062 3.33398H10.0013C9.11725 3.33398 8.2694 3.68517 7.64428 4.31029C7.01916 4.93542 6.66797 5.78326 6.66797 6.66732V33.334C6.66797 34.218 7.01916 35.0659 7.64428 35.691C8.2694 36.3161 9.11725 36.6673 10.0013 36.6673H30.0013C30.8854 36.6673 31.7332 36.3161 32.3583 35.691C32.9834 35.0659 33.3346 34.218 33.3346 33.334V14.1624C33.3346 13.632 33.1239 13.1233 32.7488 12.7482L23.9204 3.91977ZM15.8313 26.984C15.3163 27.4673 14.5563 27.684 13.6713 27.684C13.4997 27.6859 13.3282 27.6758 13.158 27.654V30.0307H11.668V23.4707C12.3404 23.3703 13.0198 23.3246 13.6996 23.334C14.628 23.334 15.288 23.5107 15.733 23.8657C16.1563 24.2023 16.443 24.754 16.443 25.404C16.4413 26.0573 16.2246 26.609 15.8313 26.984ZM22.1763 29.2423C21.4763 29.824 20.4113 30.1007 19.1096 30.1007C18.3296 30.1007 17.778 30.0507 17.403 30.0007V23.4723C18.0756 23.3741 18.7549 23.3279 19.4346 23.334C20.6963 23.334 21.5163 23.5607 22.1563 24.044C22.848 24.5573 23.2813 25.3757 23.2813 26.5507C23.2813 27.8223 22.8163 28.7007 22.1763 29.2423ZM28.3346 24.6173H25.7813V26.1357H28.168V27.359H25.7813V30.0323H24.2713V23.384H28.3346V24.6173ZM23.3346 15.0007H21.668V6.66732L30.0013 15.0007H23.3346Z" fill="#DB291F" />
                      </svg>
                      <span>{values.fichier.name}</span></div>
                    <div className="kl-circle-xmark">
                      <FontAwesomeIcon icon={faCircleXmark} onClick={() => setFieldValue("fichier", null)} style={{ cursor: "pointer" }} />
                    </div>
                  </div>
                )}
              </div>

              <div className="kl-import-document kl-file-rib">
                <strong>RIB</strong>
                <div className="kl-display-doc">
                  <div className="d-flex justify-content-center">
                    <div className="kl-icon-file-up">
                      <svg width="33" height="32" viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M27.833 13.5865H23.9797C20.8197 13.5865 18.2463 11.0132 18.2463 7.85317V3.99984C18.2463 3.2665 17.6463 2.6665 16.913 2.6665H11.2597C7.15301 2.6665 3.83301 5.33317 3.83301 10.0932V21.9065C3.83301 26.6665 7.15301 29.3332 11.2597 29.3332H21.7397C25.8463 29.3332 29.1663 26.6665 29.1663 21.9065V14.9198C29.1663 14.1865 28.5663 13.5865 27.833 13.5865ZM15.873 18.0398C15.673 18.2398 15.4197 18.3332 15.1663 18.3332C14.913 18.3332 14.6597 18.2398 14.4597 18.0398L13.4997 17.0798V22.6665C13.4997 23.2132 13.0463 23.6665 12.4997 23.6665C11.953 23.6665 11.4997 23.2132 11.4997 22.6665V17.0798L10.5397 18.0398C10.153 18.4265 9.51301 18.4265 9.12634 18.0398C8.73967 17.6532 8.73967 17.0132 9.12634 16.6265L11.793 13.9598C11.8863 13.8798 11.9797 13.8132 12.0863 13.7598C12.113 13.7465 12.153 13.7332 12.1797 13.7198C12.2597 13.6932 12.3397 13.6798 12.433 13.6665C12.473 13.6665 12.4997 13.6665 12.5397 13.6665C12.6463 13.6665 12.753 13.6932 12.8597 13.7332C12.873 13.7332 12.873 13.7332 12.8863 13.7332C12.993 13.7732 13.0997 13.8532 13.1797 13.9332C13.193 13.9465 13.2063 13.9465 13.2063 13.9598L15.873 16.6265C16.2597 17.0132 16.2597 17.6532 15.873 18.0398Z" fill="#5379F6" />
                        <path d="M23.7399 11.7468C25.0066 11.7602 26.7666 11.7602 28.2732 11.7602C29.0332 11.7602 29.4332 10.8668 28.8999 10.3335C26.9799 8.40015 23.5399 4.92015 21.5666 2.94682C21.0199 2.40015 20.0732 2.77349 20.0732 3.53349V8.18682C20.0732 10.1335 21.7266 11.7468 23.7399 11.7468Z" fill="#5379F6" />
                      </svg>
                    </div>
                  </div>
                  <div className="kl-text-import">
                    <h2>Importer un document</h2><span>Max. 12 MB</span>
                  </div>
                  <label htmlFor="rib" className="btn btn-secondary kl-btn-file-secondary">

                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12.7164 18.5417H7.28307C3.19141 18.5417 1.44141 16.7917 1.44141 12.7001V12.5917C1.44141 8.89173 2.89974 7.1084 6.16641 6.80006C6.49974 6.77506 6.81641 7.02506 6.84974 7.36673C6.88307 7.7084 6.63307 8.01673 6.28307 8.05006C3.66641 8.29173 2.69141 9.52506 2.69141 12.6001V12.7084C2.69141 16.1001 3.89141 17.3001 7.28307 17.3001H12.7164C16.1081 17.3001 17.3081 16.1001 17.3081 12.7084V12.6001C17.3081 9.5084 16.3164 8.27506 13.6497 8.05006C13.3081 8.01673 13.0497 7.71673 13.0831 7.37506C13.1164 7.0334 13.4081 6.77506 13.7581 6.8084C17.0747 7.09173 18.5581 8.8834 18.5581 12.6084V12.7167C18.5581 16.7917 16.8081 18.5417 12.7164 18.5417Z" fill="#5379F6" />
                      <path d="M10 13.1249C9.65833 13.1249 9.375 12.8416 9.375 12.4999V3.0166C9.375 2.67493 9.65833 2.3916 10 2.3916C10.3417 2.3916 10.625 2.67493 10.625 3.0166V12.4999C10.625 12.8416 10.3417 13.1249 10 13.1249Z" fill="#5379F6" />
                      <path d="M12.7912 5.50003C12.6329 5.50003 12.4745 5.4417 12.3495 5.3167L9.99954 2.9667L7.64954 5.3167C7.40788 5.55837 7.00788 5.55837 6.76621 5.3167C6.52454 5.07503 6.52454 4.67503 6.76621 4.43337L9.55788 1.6417C9.79954 1.40003 10.1995 1.40003 10.4412 1.6417L13.2329 4.43337C13.4745 4.67503 13.4745 5.07503 13.2329 5.3167C13.1162 5.4417 12.9495 5.50003 12.7912 5.50003Z" fill="#5379F6" />
                    </svg>
                    <span>Importer</span></label>
                  <Field id="rib" name="rib" component={FileInput} style={{ display: "none" }} />
                  <ErrorMessage name="rib" component="small" className="text-danger mt-3" />
                </div>
                {values.rib && (
                  <div className="kl-display-pdf-2">
                    <div className="kl-content-display">

                      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13.7782 24.4668C13.4715 24.4668 13.2649 24.4968 13.1582 24.5268V26.4901C13.2849 26.5201 13.4432 26.5285 13.6615 26.5285C14.4599 26.5285 14.9515 26.1251 14.9515 25.4435C14.9515 24.8335 14.5282 24.4668 13.7782 24.4668ZM19.5899 24.4868C19.2565 24.4868 19.0399 24.5168 18.9115 24.5468V28.8968C19.0399 28.9268 19.2465 28.9268 19.4332 28.9268C20.7949 28.9368 21.6815 28.1868 21.6815 26.6001C21.6915 25.2168 20.8832 24.4868 19.5899 24.4868Z" fill="#DB291F" />
                        <path d="M23.9204 3.91977C23.5453 3.5447 23.0366 3.33398 22.5062 3.33398H10.0013C9.11725 3.33398 8.2694 3.68517 7.64428 4.31029C7.01916 4.93542 6.66797 5.78326 6.66797 6.66732V33.334C6.66797 34.218 7.01916 35.0659 7.64428 35.691C8.2694 36.3161 9.11725 36.6673 10.0013 36.6673H30.0013C30.8854 36.6673 31.7332 36.3161 32.3583 35.691C32.9834 35.0659 33.3346 34.218 33.3346 33.334V14.1624C33.3346 13.632 33.1239 13.1233 32.7488 12.7482L23.9204 3.91977ZM15.8313 26.984C15.3163 27.4673 14.5563 27.684 13.6713 27.684C13.4997 27.6859 13.3282 27.6758 13.158 27.654V30.0307H11.668V23.4707C12.3404 23.3703 13.0198 23.3246 13.6996 23.334C14.628 23.334 15.288 23.5107 15.733 23.8657C16.1563 24.2023 16.443 24.754 16.443 25.404C16.4413 26.0573 16.2246 26.609 15.8313 26.984ZM22.1763 29.2423C21.4763 29.824 20.4113 30.1007 19.1096 30.1007C18.3296 30.1007 17.778 30.0507 17.403 30.0007V23.4723C18.0756 23.3741 18.7549 23.3279 19.4346 23.334C20.6963 23.334 21.5163 23.5607 22.1563 24.044C22.848 24.5573 23.2813 25.3757 23.2813 26.5507C23.2813 27.8223 22.8163 28.7007 22.1763 29.2423ZM28.3346 24.6173H25.7813V26.1357H28.168V27.359H25.7813V30.0323H24.2713V23.384H28.3346V24.6173ZM23.3346 15.0007H21.668V6.66732L30.0013 15.0007H23.3346Z" fill="#DB291F" />
                      </svg>
                      <span>{values.rib.name}</span></div>
                    <div className="kl-circle-xmark">
                      <FontAwesomeIcon icon={faCircleXmark} onClick={() => setFieldValue("rib", null)} style={{ cursor: "pointer" }} />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="kl-content-exist-file-society">
              <div className="text-center kl-title-list-pdf mt-4"><span>Documents existants</span></div>
              <div className="kl-pdf-list">
                {documentData.length > 0 ? (
                  <div className="kl-file-list">
                    {documentData
                      .filter(document => document.docType === 4 || document.docType === 5)
                      .map((document, index) => {
                        return (
                          <div key={index} className="kl-display-pdf-list">
                            <div className="kl-content-display-list">
                              <button type="button" onClick={() => handleViewDocument(document)}>
                                {document.docName}
                              </button>
                            </div>
                            <div className="kl-circle-xmark">
                              <FontAwesomeIcon
                                icon={faCircleXmark}
                                onClick={() => handleDeleteDocument(document)}
                                style={{ cursor: "pointer" }}
                              />
                            </div>
                          </div>
                        );
                      })}
                  </div>
                ) : (
                  <p className="text-center">Aucun document disponible.</p>
                )}
              </div>

              <div className="text-center kl-actions-container-sct">
                <Button
                  type="submit"
                  variant={!hasSubmitted || isValid ? "primary" : "secondary"}
                  className="kl-button-valider"
                  disabled={(hasSubmitted && !isValid) || loading}
                >
                  {loading ? (
                    <>
                      <CircularProgress size={15} color="inherit" />
                      <span className="mx-2">Validation en cours...</span>
                    </>
                  ) : "Valider"}
                </Button>

              </div>

            </div>

          </form>
        )}
      </Formik>
    </section>
  );
};

export default Entreprise;
